import { PageHero, Reveal, SectionHead, PhotoFrame, EmptyState, Tag } from "@/components/Shared";
import { useCollection, fmtDate, IMG } from "@/lib/api";
import { CalendarDays, MapPin, Clock } from "lucide-react";

function EventRow({ e, tone }) {
  const d = new Date(e.date);
  const valid = !isNaN(d.getTime());
  return (
    <Reveal>
      <article className="card-line group grid gap-6 p-6 sm:grid-cols-[110px_1fr] sm:p-8" data-testid={`happening-card-${e.id}`}>
        <div className="corner-frame flex h-fit flex-col items-center justify-center border border-silver/20 bg-ink/50 px-4 py-5 sm:sticky sm:top-28">
          <span className="font-display text-4xl font-semibold metallic">{valid ? d.getDate() : "—"}</span>
          <span className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-silver-muted">
            {valid ? d.toLocaleString("en-IN", { month: "short", year: "numeric" }) : ""}
          </span>
        </div>
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <Tag tone={tone === "upcoming" ? "ember" : "silver"}>{tone === "upcoming" ? "Upcoming" : "Recently Completed"}</Tag>
            <Tag>{e.category}</Tag>
          </div>
          <h3 className="mt-4 font-display text-3xl font-semibold text-silver transition-colors group-hover:text-white">{e.title}</h3>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-silver-muted">{e.description}</p>
          <div className="mt-5 flex flex-wrap gap-6 text-xs text-silver-muted">
            <span className="inline-flex items-center gap-2"><Clock size={13} className="text-ember" /> {e.time || "TBA"}</span>
            <span className="inline-flex items-center gap-2"><MapPin size={13} className="text-ember" /> {e.venue || "TBA"}</span>
            <span className="inline-flex items-center gap-2"><CalendarDays size={13} className="text-ember" /> {fmtDate(e.date)}</span>
          </div>
        </div>
        <PhotoFrame src={e.cover_image} label={e.title} className="aspect-video sm:aspect-auto sm:h-full sm:min-h-[180px]" />
      </article>
    </Reveal>
  );
}

export default function Happening() {
  const { items, loading } = useCollection("events");
  const upcoming = (items || []).filter((e) => e.status === "upcoming");
  const completed = (items || []).filter((e) => e.status !== "upcoming");

  return (
    <main>
      <PageHero eyebrow="What Are We Up To?" title="The club," italic="right now" image={IMG.emberTexture}>
        <p className="text-sm leading-relaxed text-silver-dim sm:text-base">
          Upcoming projects, current campaigns, recent events, collaborations and announcements — this page makes the
          website feel alive, not archival. Updated through the year.
        </p>
      </PageHero>

      <section className="mx-auto max-w-7xl px-6 py-20" data-testid="happening-upcoming">
        <SectionHead eyebrow="Upcoming" title="On the" italic="horizon" />
        <div className="mt-10 space-y-6">
          {loading && <p className="text-sm text-silver-muted">Loading updates…</p>}
          {upcoming.map((e) => (
            <EventRow key={e.id} e={e} tone="upcoming" />
          ))}
          {!loading && upcoming.length === 0 && (
            <EmptyState title="Nothing announced yet" note="New projects and events will be announced here soon." />
          )}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-28" data-testid="happening-completed">
        <SectionHead eyebrow="Recently Completed" title="Just" italic="wrapped" />
        <div className="mt-10 space-y-6">
          {completed.map((e) => (
            <EventRow key={e.id} e={e} tone="completed" />
          ))}
          {!loading && completed.length === 0 && (
            <EmptyState title="No completed events yet" note="Recaps will appear here after our first events." />
          )}
        </div>
      </section>
    </main>
  );
}
